import React from "react";
import { Head, Link, router } from "@inertiajs/react";
import { ArrowLeft, ExternalLink, Gavel, Loader2 } from "lucide-react";

import { StatutoryChangesPane } from "@/components/legislation_monitor/StatutoryChangesPane";
import { PlatformConsoleBreadcrumb } from "@/components/platform_console/PlatformConsoleBreadcrumb";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { StatutoryAnalysis, StatutoryAnalysisStatus } from "@/types/legislationMonitor";
import {
  addWatchId,
  isActiveAnalysis,
  readWatchIds,
  removeWatchId,
  shouldUnwatchAnalysis,
  statusVariant,
} from "@/lib/opsLegislationMonitorPolling";

type BillSummary = {
  id: number;
  name: string;
  state_abbreviation: string;
  document_type: string;
  madmin_url: string;
};

type OpsBillAnalysis = StatutoryAnalysis & {
  id: number | null;
  progress_label?: string | null;
  error_message: string | null;
  updated_at: string | null;
};

type ListContext = {
  state?: string;
  page?: string;
  per_page?: string;
};

type LegislationMonitorBillShowProps = {
  bill: BillSummary;
  analysis: OpsBillAnalysis | null;
  list_context: ListContext;
  notice?: string | null;
  alert?: string | null;
};

const POLL_INTERVAL_MS = 4000;

function listHref(context: ListContext) {
  const params = new URLSearchParams();
  if (context.state) params.set("state", context.state);
  if (context.page) params.set("page", context.page);
  if (context.per_page) params.set("per_page", context.per_page);
  const query = params.toString();
  return query ? `/ops/legislation-monitor?${query}` : "/ops/legislation-monitor";
}

function toStatutoryAnalysis(analysis: OpsBillAnalysis | null): StatutoryAnalysis | null {
  if (!analysis) return null;

  return {
    status: analysis.status as StatutoryAnalysisStatus,
    gist: analysis.gist ?? null,
    law_as_it_is: analysis.law_as_it_is ?? null,
    law_as_it_will_be: analysis.law_as_it_will_be ?? null,
    unresolved: analysis.unresolved ?? [],
    caveats: analysis.caveats ?? [],
    generated_at: analysis.generated_at ?? null,
  };
}

export default function LegislationMonitorBillShow({
  bill,
  analysis,
  list_context,
  notice,
  alert,
}: LegislationMonitorBillShowProps) {
  const [isEnqueueing, setIsEnqueueing] = React.useState(false);
  const [watchIds, setWatchIds] = React.useState<number[]>(() => readWatchIds());
  const status = analysis?.status ?? null;
  const watched = watchIds.includes(bill.id);
  const isRunning = isActiveAnalysis(status, watched, analysis?.updated_at ?? null) || isEnqueueing;
  const statutoryAnalysis = toStatutoryAnalysis(analysis);

  React.useEffect(() => {
    if (!shouldUnwatchAnalysis(status, watched, analysis?.updated_at ?? null)) return;

    setWatchIds(removeWatchId(bill.id));
  }, [bill.id, status, watched, analysis?.updated_at]);

  React.useEffect(() => {
    if (!isRunning) return;

    const intervalId = window.setInterval(() => {
      router.reload({ only: ["analysis", "bill"] });
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isRunning]);

  const onPrecompute = () => {
    setIsEnqueueing(true);
    setWatchIds(addWatchId(bill.id));
    router.post(
      `/ops/legislation-monitor/documents/${bill.id}/precompute`,
      {
        from: "show",
        state: list_context.state ?? bill.state_abbreviation,
        page: list_context.page,
        per_page: list_context.per_page,
      },
      {
        preserveScroll: true,
        onFinish: () => setIsEnqueueing(false),
      }
    );
  };

  return (
    <>
      <Head title={`${bill.name} — Legislation Monitor`} />

      <div className="container mx-auto max-w-5xl p-6">
        <div className="space-y-6">
          <PlatformConsoleBreadcrumb section="internal_tools" current="Legislation Monitor" />

          <Button variant="ghost" className="px-0" asChild>
            <Link href={listHref(list_context)}>
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
              Back to bills
            </Link>
          </Button>

          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <h1 className="flex items-center gap-2 text-3xl font-bold">
                <Gavel className="h-8 w-8" />
                {bill.name}
              </h1>
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <span>{bill.state_abbreviation}</span>
                <span>·</span>
                <span>{bill.document_type}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={statusVariant(status ?? "none")}>{status ?? "none"}</Badge>
              {analysis?.progress_label ? (
                <span className="text-sm text-muted-foreground">{analysis.progress_label}</span>
              ) : null}
              <Button type="button" size="sm" disabled={isRunning || isEnqueueing} onClick={onPrecompute}>
                {isEnqueueing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enqueueing
                  </>
                ) : isRunning ? (
                  "Running"
                ) : (
                  "Precompute"
                )}
              </Button>
              <Button type="button" size="sm" variant="outline" asChild>
                <a href={bill.madmin_url} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Madmin
                </a>
              </Button>
            </div>
          </div>

          {alert ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{alert}</AlertDescription>
            </Alert>
          ) : null}

          {notice ? (
            <Alert>
              <AlertDescription>{notice}</AlertDescription>
            </Alert>
          ) : null}

          {analysis?.error_message ? (
            <Alert variant="destructive">
              <AlertDescription>{analysis.error_message}</AlertDescription>
            </Alert>
          ) : null}

          <StatutoryChangesPane
            analysis={statutoryAnalysis}
            failedMessage={
              analysis?.error_message
                ? `Statutory analysis failed: ${analysis.error_message}`
                : "Statutory analysis could not be generated."
            }
          />
        </div>
      </div>
    </>
  );
}
